import { React, useContext, useEffect, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { motion } from "framer-motion";
import { Form } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import ApiConfig from "../../api/ApiConfig";
import { simplePostCall } from "../../api/ApiServices";
import { notifyError } from "../../sharedComponent/notify";
import Loader from "../../sharedComponent/Loader";
import NoDataComp from "../../sharedComponent/NoDataComp";
import { money } from "./ffUi";

// FF-12: client-side net balance (debit - credit) rendered as "x Dr" / "x Cr"
const netBalance = (debit, credit) => {
  const net = (Number(debit) || 0) - (Number(credit) || 0);
  if (net > 0) return `${money(net)} Dr`;
  if (net < 0) return `${money(-net)} Cr`;
  return "0.00";
};

const animations = {
  initial: { opacity: 0, x: 400 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: 100 },
};

const TrialBalance = () => {
  const { sidebar } = useContext(AppContext);
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [isBalanced, setIsBalanced] = useState(null);
  const [totals, setTotals] = useState({ debit: 0, credit: 0 });
  // FF-12: optional period filter sent to the API
  const [period, setPeriod] = useState({ from_date: "", to_date: "" });

  useEffect(() => {
    getTrialBalance();
  }, []);

  // Per-row debit/credit may come under different keys depending on backend
  const getDebit = (r) =>
    Number(
      r.debit ?? r.debit_amount ?? r.total_debit ?? r.debit_balance ?? 0
    ) || 0;
  const getCredit = (r) =>
    Number(
      r.credit ?? r.credit_amount ?? r.total_credit ?? r.credit_balance ?? 0
    ) || 0;

  // FF-12: prefer the net balance the API returns; fall back to debit - credit
  const netOf = (r) => {
    const api = r.net_balance ?? r.net ?? r.balance;
    if (api !== undefined && api !== null && api !== "" && !Number.isNaN(Number(api))) {
      const n = Number(api);
      if (n > 0) return `${money(n)} Dr`;
      if (n < 0) return `${money(-n)} Cr`;
      return "0.00";
    }
    return netBalance(getDebit(r), getCredit(r));
  };

  const getTrialBalance = (p = period) => {
    if (p.from_date && p.to_date && p.from_date > p.to_date) {
      notifyError("From date must be on or before To date");
      return;
    }
    setLoading(true);
    const body = {};
    if (p.from_date) body.from_date = p.from_date;
    if (p.to_date) body.to_date = p.to_date;
    simplePostCall(ApiConfig.FLEET_FINANCE_TRIAL_BALANCE, JSON.stringify(body))
      .then((data) => {
        if (data?.success || data?.result) {
          const d = data?.data;
          const list = Array.isArray(d) ? d : d?.accounts || d?.rows || [];
          setRows(list);
          // is_balanced lives at the top level (sibling of data)
          setIsBalanced(
            data?.is_balanced ?? d?.is_balanced ?? null
          );
          // totals live in a top-level `totals` object: { total_debit, total_credit }
          const totalsObj = data?.totals || d?.totals || {};
          setTotals({
            debit:
              totalsObj.total_debit ??
              data?.total_debit ??
              list.reduce((s, r) => s + getDebit(r), 0),
            credit:
              totalsObj.total_credit ??
              data?.total_credit ??
              list.reduce((s, r) => s + getCredit(r), 0),
          });
        } else {
          notifyError(data?.message || "Unable to load trial balance");
          setRows([]);
        }
      })
      .catch((err) => {
        console.log("trial balance err", err);
        setRows([]);
      })
      .finally(() => setLoading(false));
  };

  return (
    <motion.div
      className={sidebar ? "taskMain " : "cx-active taskMain"}
      id="cx-main"
      variants={animations}
      initial="initial"
      animate="animate"
      exit="exit"
      transition={{ duration: 0.1 }}
    >
      <div id="cx-wrapper" className="Fleet_Finance_TrialBalance">
        <div className="main-master-wrapper">
          <div
            className="d-flex justify-content-between align-items-center flex-wrap"
            style={{
              borderBottom: "0.5px solid #f6efe9",
              paddingBottom: 12,
              marginBottom: 20,
              gap: 12,
            }}
          >
            <div className="vehicleHead">
              <p style={{ borderBottom: "none", paddingBottom: 0, marginBottom: 0 }}>
                {t("Trial Balance")}
              </p>
            </div>
            {isBalanced !== null && (
              <span
                style={{
                  color: isBalanced ? "green" : "red",
                  fontWeight: 600,
                }}
              >
                {isBalanced ? t("Balanced") : t("Not Balanced")}
              </span>
            )}
            {/* FF-12: period filter */}
            <div className="d-flex align-items-center" style={{ gap: 8, marginLeft: "auto", flexWrap: "wrap" }}>
              <Form.Control
                type="date"
                size="sm"
                value={period.from_date}
                max={period.to_date || undefined}
                onChange={(e) => setPeriod((p) => ({ ...p, from_date: e.target.value }))}
                style={{ width: 160 }}
              />
              <span>{t("to")}</span>
              <Form.Control
                type="date"
                size="sm"
                value={period.to_date}
                min={period.from_date || undefined}
                onChange={(e) => setPeriod((p) => ({ ...p, to_date: e.target.value }))}
                style={{ width: 160 }}
              />
              <button type="button" className="cx-btn-2" style={{ padding: "4px 12px" }} onClick={() => getTrialBalance(period)}>
                {t("Apply")}
              </button>
              {(period.from_date || period.to_date) && (
                <button
                  type="button"
                  className="cx-btn-1"
                  style={{ padding: "4px 12px" }}
                  onClick={() => {
                    const cleared = { from_date: "", to_date: "" };
                    setPeriod(cleared);
                    getTrialBalance(cleared);
                  }}
                >
                  {t("All time")}
                </button>
              )}
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <div id="scroll_insideThe_Padding_tabel">
              <div className="VA_table">
                <table className="table tableAdmin">
                  <thead className="tableHead">
                    <tr>
                      <th>{t("Code")}</th>
                      <th>{t("Account Name")}</th>
                      <th>{t("Type")}</th>
                      <th>{t("Debit")}</th>
                      <th>{t("Credit")}</th>
                      <th>{t("Net Balance")}</th>
                    </tr>
                  </thead>
                  <tbody className="tableBody">
                    {rows && rows.length > 0 ? (
                      <>
                        {rows.map((r, i) => (
                          <tr key={r.gl_account_id || i}>
                            <td>{r.account_code}</td>
                            <td>{r.account_name}</td>
                            <td>{r.account_type}</td>
                            <td>{getDebit(r).toLocaleString()}</td>
                            <td>{getCredit(r).toLocaleString()}</td>
                            {/* FF-12 */}
                            <td>{netOf(r)}</td>
                          </tr>
                        ))}
                        <tr style={{ fontWeight: 700 }}>
                          <td colSpan={3}>{t("Total")}</td>
                          <td>{Number(totals.debit).toLocaleString()}</td>
                          <td>{Number(totals.credit).toLocaleString()}</td>
                          <td>{netBalance(totals.debit, totals.credit)}</td>
                        </tr>
                      </>
                    ) : (
                      <tr>
                        <td colSpan={6}>
                          <NoDataComp />
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default TrialBalance;
